// Pure, dependency-free extraction helpers shared by the order form (bundled by
// react-scripts/webpack) and the Node test suite (plain CommonJS). No React, no
// DOM, no network — just merging/ranking of per-file extraction results.

// Confidence bands (tunable). Raw scores are 0..1, returned per field by the
// read-document function.
const CONFIDENCE = {
  HIGH: 0.85,
  MEDIUM: 0.65,
};

// Map a raw score to "high" | "medium" | "low".
function bandConfidence(score) {
  if (typeof score !== "number" || isNaN(score)) return "low";
  if (score >= CONFIDENCE.HIGH) return "high";
  if (score >= CONFIDENCE.MEDIUM) return "medium";
  return "low";
}

// Merge extraction results from multiple uploaded documents into one candidate
// set per field. Higher-confidence values are preferred; identical values are
// collapsed (case-insensitive); a field with two or more distinct non-low
// candidates is flagged as a conflict for the customer to resolve.
//
// perFileResults: Array<{ file: string, fields: { [key]: {value, confidence, found} } }>
// returns: { [key]: {
//   best: {value, confidence, level, file} | null,
//   candidates: Array<{value, confidence, level, file}>,
//   conflict: boolean
// } }
function mergeExtractions(perFileResults) {
  const merged = {};
  const list = Array.isArray(perFileResults) ? perFileResults : [];

  for (const result of list) {
    if (!result || !result.fields) continue;
    const file = result.file || "";
    for (const key of Object.keys(result.fields)) {
      const f = result.fields[key];
      if (!f || !f.found) continue;
      const value = String(f.value == null ? "" : f.value).trim();
      if (!value) continue;
      const confidence = typeof f.confidence === "number" ? f.confidence : 0;
      if (!merged[key]) merged[key] = { candidates: [] };
      merged[key].candidates.push({
        value,
        confidence,
        level: bandConfidence(confidence),
        file,
      });
    }
  }

  for (const key of Object.keys(merged)) {
    const sorted = merged[key].candidates.slice().sort((a, b) => b.confidence - a.confidence);
    // Collapse identical values, keeping the highest-confidence instance.
    const seen = new Map();
    for (const c of sorted) {
      const norm = c.value.toLowerCase().replace(/\s+/g, " ").trim();
      if (!seen.has(norm)) seen.set(norm, c);
    }
    const unique = Array.from(seen.values());
    merged[key].candidates = unique;
    merged[key].best = unique[0] || null;
    const strong = unique.filter((c) => c.level !== "low");
    merged[key].conflict = strong.length >= 2;
  }

  return merged;
}

module.exports = { CONFIDENCE, bandConfidence, mergeExtractions };
